import _ from 'lodash'
import Ajv from 'ajv'
import jsonMap from 'json-source-map'
import jsonlint from 'jsonlint-mod'

import { ValidationError } from './error'

/* eslint-disable prefer-destructuring */
/* eslint-disable no-param-reassign */
/* eslint-disable no-eval */

/**
 * Обертка над JSON
 */
class JSONWrapper {
  /**
   * constructor
   *
   * @param {(string|Object)} data JSON-строка или объект
   * @param {Array.<string>} props Свойства, которые надо поставить в null.
   * Необходимо для проверки, что никакие ключи JSON'а не совпадают со свойствами класса
   */
  constructor(data, props, appendModules) {
    this.keys = []
    this.ajv = null
    this.validate = null
    this.map = null
    this.module = null
    this.schemas = []
    this.appendModules = appendModules || []
    if (props) {
      props.forEach((prop) => {
        this[prop] = null
      })
    }

    if (_.isString(data)) {
      this.object = JSON.parse(data)
    } else {
      this.object = data
    }

    this.ajvOptions = {
      allErrors: true,
      jsonPointers: true
    }
  }

  /**
   * setSchemas
   *
   * @param {object} mainSchema Основная JSON-схема
   * @param {Array.<Object>} addSchemas Список из дополнительных схем, на которые
   * ссылается основная через $ref
   */
  setSchemas(mainSchema, addSchemas) {
    addSchemas = addSchemas || []
    this.schemas = [mainSchema, ...addSchemas]
    this.ajv = new Ajv({ schemas: this.schemas, ...this.ajvOptions })
    this.validate = this.ajv.compile(mainSchema)
  }

  /**
   * Валидация объекта по установленной JSON-схеме
   *
   * @param {Object} object Объект. Если нет, то проверяется класс
   * @returns {Array.<ValidationError>} Список ошибок
   */
  validateSchema(object) {
    object = object || this.object
    const valid = this.validate(object)
    const errors = []
    if (!valid) {
      this.validate.errors.forEach((error) => errors.push(
        new ValidationError(
          `${error.message} (${JSON.stringify(error.params)})`,
          null,
          error.dataPath,
        ),
      ))
    }
    return errors
  }

  /**
   * Валидация объекта. Точка расширения для наследников.
   *
   * @param {Object} object Объект для валидации. Если нет, то проверяется класс
   * @param {Object} params Параметры валидации
   * @returns {Array.<ValidationError>} Список ошибок
   */
  validateObject(object, params) { // eslint-disable-line no-unused-vars
    object = object || this.object
    return this.validateSchema(object)
  }

  /**
   * Валидация объекта с возвращением исключения при валидации как ошибки
   *
   * @param {Object} object
   * @param {Object} params
   * @returns {Array.<ValidationError>}
   */
  safeValidateObject(object, params) {
    try {
      return this.validateObject(object, params)
    } catch (err) {
      console.error(err)
      const error = new ValidationError(`Ошибка при валидации\n${err.stack}`, null, '')
      return [error]
    }
  }

  /**
   * Валидация JSON-строки. Если строка может быть прочитана как JSON, вызывает
   * {@link JSONWrapper.validateObject}.
   * Если имеется ошибка разбора, будет возвращена одна ошибка, у которой стоит атрибут
   * jsonError: true
   *
   * @param {string} string json-строка
   * @param {Object} params параметры валидации
   * @returns {Array.<ValidationError>} Список ошибок.
   */
  validateString(string, params) {
    let object
    try {
      object = jsonlint.parse(string)
    } catch (err) {
      const error = new ValidationError(`Ошибка разбора:\n${err.message}`, null, '')
      error.jsonError = true
      return [error]
    }
    return this.safeValidateObject(object, params)
  }

  /**
   * Конвертация из массива строк в строку
   *
   * @static
   * @param {Array.<string>} multistring
   * @returns {string}
   */
  static fromMultiString(multistring) {
    return multistring.join('\n')
  }

  /**
   * Конвертация из строки в массив строк
   *
   * @static
   * @param {string} string
   * @returns {Array.<string>}
   */
  static toMultiString(string) {
    return string.split('\n')
  }

  /**
   * Импорт блока с кодом в свойство 'module'
   *
   * @param {function} accessor (объект) => нужное свойство объекта
   */
  importCode(accessor) {
    let code = accessor(this)
    if (code) {
      if (Array.isArray(code)) {
        code = JSONWrapper.fromMultiString(code)
      }
      try {
        this.module = eval(code)
      } catch (e) {
        console.error('Code was not imported')
        console.error(e)
      }
    }
    this.appendModules.forEach((module) => {
      if (!this.module) {
        this.module = module
      } else {
        Object.assign(this.module, module)
      }
    })
  }

  /**
   * Вернуть блок с кодом
   *
   * @param {function} accessor (объект) => нужное свойство объекта
   */
  getCodeString(accessor) {
    let code = accessor(this)
    if (code) {
      if (Array.isArray(code)) {
        code = JSONWrapper.fromMultiString(code)
      }
      return code
    }
    return ''
  }

  /**
   * Проверка, есть ли в module.exports сегмента с кодом объект с данным именем
   *
   * @param {string} name
   * @returns {boolean}
   */
  moduleHasExport(name, module) {
    module = module || this.module
    if (!module) {
      return false
    }
    return Boolean(module[name])
  }

  /**
   * Получить объект (набор свойств, которые поступали на вход set object)
   *
   * @returns {object}
   */
  get object() {
    return _.pick(this, this.keys)
  }

  /**
   * Будет вызвана после установки объекта в set object
   *
   */
  afterObjectSet() {} // eslint-disable-line class-methods-use-this

  /**
   * Проверка, нет ли в классе свойств, совпадающих с ключами объекта
   *
   * @param {Object} object
   */
  checkKeys(object) {
    object = object || this.object
    const keys = Array.from(Object.keys(object))
    keys.forEach((key) => {
      if ((!this.keys || !this.keys.includes(key))
        && Object.prototype.hasOwnProperty.call(this, key)) {
        throw new Error(`Object property ${key} shadows a class one`)
      }
    })
  }

  /**
   * Установка нового объекта. Предшествующий удаляется
   *
   * @param {Object} newObject
   */
  set object(newObject) {
    if (this.keys) {
      this.keys.forEach((key) => {
        delete this[key]
      })
      this.keys = null
    }
    if (newObject.$schema) {
      delete newObject.$schema
    }
    this.checkKeys(newObject)
    this.keys = Array.from(Object.keys(newObject))
    Object.assign(this, newObject)
    this.afterObjectSet()
  }

  /**
   * Установить атрибут в объект (возвращаемый по get object)
   *
   * @param {string} key
   * @param {any} value
   */
  setAttr(key, value) {
    this[key] = value
    if (!this.keys.includes(key)) {
      this.keys.push(key)
    }
  }

  /**
   * Конвертация объекта в форматированную JSON-строку
   *
   * @returns {string}
   */
  get json() {
    return JSON.stringify(this.object, null, 4)
  }

  /**
   * Конвертация объекта в форматированную JSON-строку и карту
   * Вид карты: { jsonPonter: { <location>: { line: number, column: number, pos: number } } }
   * <location>: 'value', 'valueEnd', 'key', 'keyEnd'
   *
   * @returns {Object} Объект с json и pointers
   */
  toMapJSON() {
    return jsonMap.stringify(this.object, null, 4)
  }
}

export default JSONWrapper
